import React from "react";
import {
  LuChartNoAxesColumn,
  LuShieldCheck,
  LuSlidersHorizontal,
} from "react-icons/lu";
import { useLanguage } from "../context/LanguageContext";
import { useConsent } from "../consent/ConsentContext";
import "../styles/ConsentManager.scss";

const CookieBanner = () => {
  const { t } = useLanguage();
  const {
    consent,
    hasConsented,
    isPreferencesOpen,
    acceptNecessaryAndFunctional,
    acceptAll,
    savePreferences,
    closePreferences,
  } = useConsent();
  const [isCustomizing, setIsCustomizing] = React.useState(false);
  const [draft, setDraft] = React.useState({
    functional: Boolean(consent?.functional),
    analytics: Boolean(consent?.analytics),
  });

  const isOpen = !hasConsented || isPreferencesOpen;
  const allowBackdropClose = hasConsented && isPreferencesOpen;

  React.useEffect(() => {
    setDraft({
      functional: Boolean(consent?.functional),
      analytics: Boolean(consent?.analytics),
    });
  }, [consent]);

  React.useEffect(() => {
    if (isPreferencesOpen) {
      setIsCustomizing(true);
    } else if (hasConsented) {
      setIsCustomizing(false);
    }
  }, [hasConsented, isPreferencesOpen]);

  React.useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined;
    }

    document.body.classList.add("cookie-banner-open");
    return () => {
      document.body.classList.remove("cookie-banner-open");
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || !hasConsented) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closePreferences();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closePreferences, hasConsented, isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleToggle = (key) => {
    setDraft((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const handleSave = () => {
    savePreferences(draft);
  };

  return (
    <div
      className="cookie-banner-backdrop"
      role="presentation"
      onClick={() => {
        if (allowBackdropClose) {
          closePreferences();
        }
      }}
    >
      <section
        className={`cookie-banner${isCustomizing ? " cookie-banner--expanded" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-banner-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="cookie-banner__header">
          <div className="cookie-banner__eyebrow">
            <LuShieldCheck aria-hidden="true" />
            <span>{t("cookieBanner.eyebrow", "Cookie")}</span>
          </div>
          {hasConsented && (
            <button
              type="button"
              className="cookie-banner__close"
              onClick={closePreferences}
              aria-label={t("cookieBanner.actions.close", "Close cookie settings")}
            >
              ×
            </button>
          )}
          <h2 id="cookie-banner-title">
            {t("cookieBanner.title", "Choose how Wayli uses cookies")}
          </h2>
          <p className="cookie-banner__lead">
            {t(
              "cookieBanner.lead",
              "We use necessary cookies to keep sign-in, booking recovery, and basic preferences working. Choose necessary plus functional features for maps, or allow all to also enable site analytics."
            )}
          </p>
        </header>

        {!isCustomizing && (
          <div className="cookie-banner__summary">
            <div className="cookie-pill">
              <LuShieldCheck aria-hidden="true" />
              <div>
                <strong>{t("cookieBanner.categories.necessary.label", "Necessary")}</strong>
                <span>
                  {t(
                    "cookieBanner.categories.necessary.summary",
                    "Sign-in, booking recovery, language, and your consent choice."
                  )}
                </span>
              </div>
            </div>
            <div className="cookie-pill">
              <LuSlidersHorizontal aria-hidden="true" />
              <div>
                <strong>{t("cookieBanner.categories.functional.label", "Functional")}</strong>
                <span>
                  {t(
                    "cookieBanner.categories.functional.summary",
                    "Google Maps and similar optional features."
                  )}
                </span>
              </div>
            </div>
            <div className="cookie-pill">
              <LuChartNoAxesColumn aria-hidden="true" />
              <div>
                <strong>{t("cookieBanner.categories.analytics.label", "Analytics")}</strong>
                <span>
                  {t(
                    "cookieBanner.categories.analytics.summary",
                    "Visit measurement for our own traffic reports."
                  )}
                </span>
              </div>
            </div>
          </div>
        )}

        {isCustomizing && (
          <div className="cookie-banner__grid">
            <article className="cookie-card cookie-card--locked">
              <div className="cookie-card__header">
                <div>
                  <p>{t("cookieBanner.categories.necessary.label", "Necessary")}</p>
                  <h3>{t("cookieBanner.categories.necessary.title", "Always active")}</h3>
                </div>
                <span className="cookie-card__badge">
                  {t("cookieBanner.categories.necessary.badge", "Required")}
                </span>
              </div>
              <p>
                {t(
                  "cookieBanner.categories.necessary.description",
                  "Needed for login state, booking recovery after payment redirects, language preference, and saving your cookie choice."
                )}
              </p>
            </article>

            <article className="cookie-card">
              <div className="cookie-card__header">
                <div>
                  <p>{t("cookieBanner.categories.functional.label", "Functional")}</p>
                  <h3>{t("cookieBanner.categories.functional.title", "Maps and analytics")}</h3>
                </div>
                <label
                  className={`cookie-toggle${draft.functional ? " is-enabled" : ""}`}
                >
                  <input
                    type="checkbox"
                    aria-label={t(
                      "cookieBanner.categories.functional.toggle",
                      "Toggle functional cookies"
                    )}
                    checked={draft.functional}
                    onChange={() => handleToggle("functional")}
                  />
                  <span className="cookie-toggle__track" aria-hidden="true" />
                </label>
              </div>
              <p>
                {t(
                  "cookieBanner.categories.functional.description",
                  "Allows Wayli to load embedded Google Maps when you want the map view."
                )}
              </p>
            </article>

            <article className="cookie-card">
              <div className="cookie-card__header">
                <div>
                  <p>{t("cookieBanner.categories.analytics.label", "Analytics")}</p>
                  <h3>{t("cookieBanner.categories.analytics.title", "Site analytics")}</h3>
                </div>
                <label
                  className={`cookie-toggle${draft.analytics ? " is-enabled" : ""}`}
                >
                  <input
                    type="checkbox"
                    aria-label={t(
                      "cookieBanner.categories.analytics.toggle",
                      "Toggle analytics cookies"
                    )}
                    checked={draft.analytics}
                    onChange={() => handleToggle("analytics")}
                  />
                  <span className="cookie-toggle__track" aria-hidden="true" />
                </label>
              </div>
              <p>
                {t(
                  "cookieBanner.categories.analytics.description",
                  "Allows Wayli to measure page visits using a local identifier and technical visit data sent to our server."
                )}
              </p>
            </article>
          </div>
        )}

        <div className="cookie-banner__actions">
          {isCustomizing ? (
            <button
              type="button"
              className="cookie-banner__button cookie-banner__button--outline"
              onClick={handleSave}
            >
              {t("cookieBanner.actions.save", "Save choices")}
            </button>
          ) : (
            <button
              type="button"
              className="cookie-banner__button cookie-banner__button--outline"
              onClick={acceptNecessaryAndFunctional}
            >
              {t(
                "cookieBanner.actions.acceptFunctional",
                "Necessary + functional"
              )}
            </button>
          )}
          <button
            type="button"
            className="cookie-banner__button cookie-banner__button--primary"
            onClick={acceptAll}
          >
            {t("cookieBanner.actions.accept", "All")}
          </button>
        </div>

        <p className="cookie-banner__footnote">
          {t(
            "cookieBanner.footnote",
            "You can change this any time from the footer."
          )}
        </p>
      </section>
    </div>
  );
};

export default CookieBanner;
